import * as THREE from 'three';

/** Procedural canvas textures — VOIDLINE ships zero external assets. */

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function toTexture(c: HTMLCanvasElement, repeat = true): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  if (repeat) {
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
  }
  t.anisotropy = 4;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Track deck: dark plating with subtle panel lines and centerline hints. */
export function deckTexture(): THREE.CanvasTexture {
  const [c, g] = canvas(256, 256);
  g.fillStyle = '#101318';
  g.fillRect(0, 0, 256, 256);
  // panel blocks
  for (let y = 0; y < 4; y++) {
    for (let x = 0; x < 4; x++) {
      const shade = 14 + ((x * 7 + y * 13) % 9);
      g.fillStyle = `rgb(${shade},${shade + 2},${shade + 6})`;
      g.fillRect(x * 64 + 1, y * 64 + 1, 62, 62);
    }
  }
  // fine seams
  g.strokeStyle = 'rgba(0,0,0,0.55)';
  g.lineWidth = 2;
  for (let i = 0; i <= 4; i++) {
    g.beginPath();
    g.moveTo(i * 64, 0);
    g.lineTo(i * 64, 256);
    g.stroke();
    g.beginPath();
    g.moveTo(0, i * 64);
    g.lineTo(256, i * 64);
    g.stroke();
  }
  // subtle scuffs
  for (let i = 0; i < 260; i++) {
    const x = Math.random() * 256;
    const y = Math.random() * 256;
    g.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
    g.fillRect(x, y, Math.random() * 8, 1);
  }
  return toTexture(c);
}

/** Boost pad chevrons (animated by scrolling UVs). */
export function boostTexture(): THREE.CanvasTexture {
  const [c, g] = canvas(128, 256);
  g.fillStyle = 'rgba(8,24,30,0.9)';
  g.fillRect(0, 0, 128, 256);
  g.fillStyle = '#28f9d4';
  for (let i = 0; i < 4; i++) {
    const y = i * 64;
    g.beginPath();
    g.moveTo(8, y + 46);
    g.lineTo(64, y + 10);
    g.lineTo(120, y + 46);
    g.lineTo(120, y + 62);
    g.lineTo(64, y + 26);
    g.lineTo(8, y + 62);
    g.closePath();
    g.fill();
  }
  const t = toTexture(c);
  return t;
}

/** Hazard stripes for barriers. */
export function hazardTexture(): THREE.CanvasTexture {
  const [c, g] = canvas(128, 128);
  g.fillStyle = '#1a1206';
  g.fillRect(0, 0, 128, 128);
  g.fillStyle = '#e8b23a';
  g.save();
  for (let i = -4; i < 8; i++) {
    g.fillRect(i * 32, 0, 16, 128);
  }
  g.restore();
  const t2 = toTexture(c);
  t2.rotation = Math.PI / 4;
  return t2;
}

/** Start/finish checker line. */
export function finishTexture(): THREE.CanvasTexture {
  const [c, g] = canvas(256, 64);
  for (let y = 0; y < 2; y++) {
    for (let x = 0; x < 8; x++) {
      g.fillStyle = (x + y) % 2 === 0 ? '#e8f2ff' : '#0a0d14';
      g.fillRect(x * 32, y * 32, 32, 32);
    }
  }
  return toTexture(c);
}

/** Soft radial glow sprite for particles / engine flares. */
export function glowSprite(): THREE.CanvasTexture {
  const [c, g] = canvas(64, 64);
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  return toTexture(c, false);
}

/** Elongated streak sprite for sparks / rain. */
export function streakSprite(): THREE.CanvasTexture {
  const [c, g] = canvas(16, 64);
  const grad = g.createLinearGradient(0, 0, 0, 64);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.5, 'rgba(255,255,255,1)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(6, 0, 4, 64);
  return toTexture(c, false);
}

/** Capital-ship hull: dark plating with sparse lit window strips (use as map + emissiveMap). */
export function hullTexture(seed = 1): THREE.CanvasTexture {
  const [c, g] = canvas(256, 128);
  g.fillStyle = '#080a0e';
  g.fillRect(0, 0, 256, 128);
  let rng = seed;
  const rand = () => {
    rng = (rng * 16807) % 2147483647;
    return rng / 2147483647;
  };
  // plating blocks
  for (let i = 0; i < 70; i++) {
    const x = rand() * 256;
    const y = rand() * 128;
    const shade = 9 + Math.floor(rand() * 11);
    g.fillStyle = `rgb(${shade},${shade + 2},${shade + 5})`;
    g.fillRect(x, y, 12 + rand() * 52, 5 + rand() * 18);
  }
  // long seams
  g.strokeStyle = 'rgba(0,0,0,0.6)';
  g.lineWidth = 1;
  for (let i = 0; i < 9; i++) {
    const y = rand() * 128;
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(256, y);
    g.stroke();
  }
  // lit window strips
  const palette = ['#bfe3ff', '#ffe9b8', '#9afce0'];
  for (let row = 0; row < 10; row++) {
    const y = 6 + rand() * 116;
    let x = rand() * 40;
    while (x < 250) {
      if (rand() < 0.55) {
        g.fillStyle = palette[Math.floor(rand() * palette.length)];
        g.globalAlpha = 0.45 + rand() * 0.55;
        g.fillRect(x, y, 3, 2);
      }
      x += 5 + rand() * 7;
    }
  }
  g.globalAlpha = 1;
  return toTexture(c);
}

/** Soft irregular cloud puff for cloud decks / haze layers. */
export function cloudSprite(seed = 1): THREE.CanvasTexture {
  const [c, g] = canvas(128, 128);
  let rng = seed;
  const rand = () => {
    rng = (rng * 16807) % 2147483647;
    return rng / 2147483647;
  };
  for (let i = 0; i < 28; i++) {
    const x = 30 + rand() * 68;
    const y = 36 + rand() * 56;
    const r = 10 + rand() * 26;
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(255,255,255,${0.09 + rand() * 0.11})`);
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  return toTexture(c, false);
}

/** Wind-rippled sand for desert floors. */
export function duneTexture(seed = 1): THREE.CanvasTexture {
  const [c, g] = canvas(256, 256);
  g.fillStyle = '#6b5138';
  g.fillRect(0, 0, 256, 256);
  let rng = seed;
  const rand = () => {
    rng = (rng * 16807) % 2147483647;
    return rng / 2147483647;
  };
  // broad tonal patches
  for (let i = 0; i < 40; i++) {
    const shade = rand();
    g.fillStyle = shade < 0.5 ? 'rgba(40,28,16,0.10)' : 'rgba(200,160,110,0.08)';
    g.beginPath();
    g.arc(rand() * 256, rand() * 256, 20 + rand() * 60, 0, Math.PI * 2);
    g.fill();
  }
  // wind ripples: wavy horizontal shadow lines (whole cycle counts so the tile repeats)
  for (let y = 0; y < 256; y += 7) {
    const amp = 2 + rand() * 4;
    const cycles = 2 + Math.floor(rand() * 4);
    const phase = rand() * Math.PI * 2;
    g.strokeStyle = `rgba(20,12,6,${0.12 + rand() * 0.12})`;
    g.lineWidth = 1 + rand();
    g.beginPath();
    for (let x = 0; x <= 256; x += 8) {
      const yy = y + Math.sin((x / 256) * cycles * Math.PI * 2 + phase) * amp;
      if (x === 0) g.moveTo(x, yy);
      else g.lineTo(x, yy);
    }
    g.stroke();
  }
  return toTexture(c);
}

/** Window-lit building facade for city environments. */
export function facadeTexture(seed = 1): THREE.CanvasTexture {
  const [c, g] = canvas(128, 256);
  g.fillStyle = '#07080c';
  g.fillRect(0, 0, 128, 256);
  let rng = seed;
  const rand = () => {
    rng = (rng * 16807) % 2147483647;
    return rng / 2147483647;
  };
  const palette = ['#31c8ff', '#ffd25e', '#ff2d6f', '#9ac8e8', '#c9f0ff'];
  for (let y = 4; y < 250; y += 10) {
    for (let x = 4; x < 122; x += 8) {
      if (rand() < 0.42) {
        const col = palette[Math.floor(rand() * palette.length)];
        g.fillStyle = col;
        g.globalAlpha = 0.25 + rand() * 0.75;
        g.fillRect(x, y, 5, 6);
      }
    }
  }
  g.globalAlpha = 1;
  return toTexture(c);
}
